import { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { handleTokenExpiration, isAuthenticated } from '../../shared/authUtils';

/**
 * Custom hook for authentication functionality in React components
 * Provides logout functionality and authentication state
 */
export function useAuth() {
  const history = useHistory();
  const [authenticated, setAuthenticated] = useState<boolean | null>(null);

  useEffect(() => {
    const checkAuth = async () => {
      const authStatus = await isAuthenticated();
      setAuthenticated(authStatus);
    };
    checkAuth();
  }, []);

  /**
   * Handles user logout
   * Clears local token and redirects to login (without Auth0 logout redirect)
   */
  const logout = useCallback(async () => {
    try {
      console.log('🔒 Logging out user...');
      await OfficeRuntime.storage.removeItem("token");
      console.log('✅ Token removed from storage');
      setAuthenticated(false);
      history.push('/login');
      console.log('✅ Redirected to login page');
    } catch (error) {
      console.error('❌ Error during logout:', error);
      try {
        await OfficeRuntime.storage.removeItem("token");
        setAuthenticated(false);
        history.push('/login');
      } catch (fallbackError) {
        console.error('❌ Fallback logout also failed:', fallbackError);
      }
    }
  }, [history]);

  /**
   * Handles automatic logout on token expiration (401 errors)
   * This is called when API returns 401 Unauthorized
   */
  const handleExpiredToken = useCallback(async () => {
    await handleTokenExpiration('Token expired');
    setAuthenticated(false);
    history.push('/login');
  }, [history]);

  return {
    logout,
    handleExpiredToken,
    isAuthenticated: authenticated === true,
    isLoading: authenticated === null,
  };
}

